import { RefObject, useEffect } from 'react'

// While a card is dragged, the board scrolls sideways when the pointer is near its left or right
// edge, faster the closer it gets. The browser's own drag and drop doesn't do this.
export const EDGE_ZONE_PX = 96
export const MAX_STEP_PX = 28
// Browsers repeat dragover while the pointer rests, but can wait up to about half a second between
// them. With none for this long, the drag is over even if no dragend arrived (for example, the
// dragged card was redrawn elsewhere by a refresh), so scrolling stops.
export const STALE_POINTER_MS = 1000

// Pixels to scroll this frame: negative near the left edge, positive near the right, 0 between.
// Past an edge (over the sidebar, or beside the board) scrolls at full speed.
export function edgeScrollStep(x: number, left: number, right: number, zone = EDGE_ZONE_PX, max = MAX_STEP_PX): number {
  const width = right - left
  if (width <= 0) return 0
  const z = Math.min(zone, width / 4)
  if (x < left + z) return -Math.ceil(max * Math.min(1, (left + z - x) / z))
  if (x > right - z) return Math.ceil(max * Math.min(1, (x - (right - z)) / z))
  return 0
}

export function useDragEdgeScroll(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return
    let x: number | null = null
    let seenAt = 0
    let frame = 0
    // The board's edges don't move during a drag: measure once, and again if the window resizes,
    // rather than forcing a layout on every frame.
    let bounds: { left: number; right: number } | null = null
    const measure = () => {
      const r = ref.current?.getBoundingClientRect()
      bounds = r ? { left: r.left, right: r.right } : null
    }
    const onDragOver = (e: DragEvent) => {
      x = e.clientX
      seenAt = performance.now()
    }
    const onDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) x = null
    }
    const onDrop = () => {
      x = null
    }
    const tick = () => {
      if (x !== null && performance.now() - seenAt > STALE_POINTER_MS) x = null
      const el = ref.current
      if (el && bounds && x !== null) {
        const step = edgeScrollStep(x, bounds.left, bounds.right)
        if (step !== 0) el.scrollLeft += step
      }
      frame = requestAnimationFrame(tick)
    }
    measure()
    window.addEventListener('resize', measure)
    document.addEventListener('dragover', onDragOver)
    document.addEventListener('dragleave', onDragLeave)
    document.addEventListener('drop', onDrop)
    frame = requestAnimationFrame(tick)
    return () => {
      window.removeEventListener('resize', measure)
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('dragleave', onDragLeave)
      document.removeEventListener('drop', onDrop)
      cancelAnimationFrame(frame)
    }
  }, [ref, active])
}
